// packages/web-ui/src/components/kb-change-tree/kb-change-tree-helpers.ts
//
// Story W4-S4 — pure helpers ported verbatim from the Lit precedent at
// packages/webview-ui/src/panels/diff-panel/components/kb-change-tree.ts
// (lines 23–47 + 375–430). Keeping these isolated from React lets the
// parity tests assert grouping / counting / status-icon behaviour
// without the RTL plumbing.

import type {
  KbEntityChange,
  KbEntityKind,
  KbEntityState,
  KbEntityStatus,
} from '@tessa-lean/base';

/** Display order for entity kinds in the tree. Empty buckets are skipped. */
export const KIND_ORDER: KbEntityKind[] = [
  'part',
  'port',
  'interface',
  'interface-def',
  'connection',
  'connection-def',
  'value-attribute',
];

/** Human-readable, plural group titles. */
export const KIND_GROUP_LABEL: Record<KbEntityKind, string> = {
  part: 'Parts',
  port: 'Ports',
  interface: 'Interfaces',
  'interface-def': 'Interface definitions',
  connection: 'Connections',
  'connection-def': 'Connection definitions',
  'value-attribute': 'Value attributes',
};

/** Short, capitalised kind badge. */
export const KIND_BADGE_LABEL: Record<KbEntityKind, string> = {
  part: 'Part',
  port: 'Port',
  interface: 'Interface',
  'interface-def': 'Interface def',
  connection: 'Connection',
  'connection-def': 'Connection def',
  'value-attribute': 'Value',
};

/** Lookup status for an entity by id from a flat `entityStates` array. */
export function statusFor(
  entityStates: ReadonlyArray<KbEntityState>,
  entityId: string,
): KbEntityStatus {
  const found = entityStates.find((s) => s.entityId === entityId);
  return found?.status ?? 'pending';
}

/**
 * Map an entity status to its display glyph. `applied` and `accepted`
 * share the same character; CSS classes distinguish them visually.
 */
export function statusIcon(status: KbEntityStatus): string {
  switch (status) {
    case 'pending':
      return '⏸'; // ⏸
    case 'accepted':
      return '✓'; // ✓
    case 'applied':
      return '✓'; // ✓ (different CSS class)
    case 'rejected':
      return '✗'; // ✗
    case 'failed':
      return '✗'; // ✗
    case 'unresolvable':
      return '⚠'; // ⚠
    default:
      return '⏸';
  }
}

/** Group entities by kind, preserving the canonical kind order. */
export function groupedEntities(
  entities: ReadonlyArray<KbEntityChange>,
): Array<{ kind: KbEntityKind; entities: KbEntityChange[] }> {
  const buckets = new Map<KbEntityKind, KbEntityChange[]>();
  for (const entity of entities) {
    const arr = buckets.get(entity.kind) ?? [];
    arr.push(entity);
    buckets.set(entity.kind, arr);
  }
  const out: Array<{ kind: KbEntityKind; entities: KbEntityChange[] }> = [];
  for (const kind of KIND_ORDER) {
    const items = buckets.get(kind);
    if (items && items.length > 0) {
      out.push({ kind, entities: items });
    }
  }
  return out;
}

/** Footer aggregate counts, parity with Lit lines 416–430. */
export function counts(
  entities: ReadonlyArray<KbEntityChange>,
  entityStates: ReadonlyArray<KbEntityState>,
): { accepted: number; rejected: number; pending: number; failed: number } {
  let accepted = 0;
  let rejected = 0;
  let pending = 0;
  let failed = 0;
  for (const e of entities) {
    const status = statusFor(entityStates, e.entityId);
    if (status === 'accepted' || status === 'applied') accepted++;
    else if (status === 'rejected' || status === 'unresolvable') rejected++;
    else if (status === 'failed') failed++;
    else pending++;
  }
  return { accepted, rejected, pending, failed };
}

/**
 * The W4-S4 review-chip cycle (UX §10.4 line 1342):
 *   pending → accepted → rejected → pending
 *
 * Terminal statuses (`applied`, `failed`) map back to `pending` so a click
 * re-arms the row. `unresolvable` is a no-op at the chip level — only the
 * Failed-entities footer's [Retry] re-arms it (story AC #2 table).
 */
export function nextStatusInCycle(current: KbEntityStatus): KbEntityStatus | null {
  switch (current) {
    case 'pending':
      return 'accepted';
    case 'accepted':
      return 'rejected';
    case 'rejected':
      return 'pending';
    case 'applied':
      return 'pending';
    case 'failed':
      return 'pending';
    case 'unresolvable':
    default:
      return null;
  }
}

/**
 * Returns the id of the first ancestor (in `dependsOn` order) whose
 * status is `rejected` or `unresolvable`, or undefined when all
 * ancestors are non-blocking. Used both client- and server-side for
 * the FR-T7 dependency-violation guard.
 */
export function findBlockingAncestor(
  entity: Pick<KbEntityChange, 'dependsOn'>,
  entityStates: ReadonlyArray<KbEntityState>,
): string | undefined {
  return entity.dependsOn.find((depId) => {
    const status = statusFor(entityStates, depId);
    return status === 'rejected' || status === 'unresolvable';
  });
}
